/**
 * EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Phase 1
 * 
 * Crew Email Editor Modal
 * - Reusable modal for editing/previewing crew email templates
 * - Plain text email editor (no rich text)
 * - Variable insertion support
 * - Preview and send functionality
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Mail,
  Send,
  Eye,
  Variable,
  Loader2,
  X,
  ChevronDown,
  Info,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
// EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1: Help panel
import { CrewOnboardingHelpPanel } from "../guidance/CrewOnboardingHelpPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available template variables
// EF-REMOVE-TEMP-PASSWORD-AND-ENFORCE-SET-PASSWORD-V1: Removed temp_password variable
// EF-PWA-BRANDING-V1-STEP4: Updated example company name
const TEMPLATE_VARIABLES = [
  { key: "crew_firstname", description: "Crew member's first name", example: "Jean" },
  { key: "crew_lastname", description: "Crew member's last name", example: "DUPONT" },
  { key: "crew_fullname", description: "Crew member's full name", example: "Jean DUPONT" },
  { key: "crew_email", description: "Crew member's email", example: "jean@example.com" },
  { key: "company_name", description: "Company name", example: "EventsFlo" },
  { key: "portal_link", description: "Link to crew portal/onboarding", example: "https://portal.example.com" },
  { key: "missing_documents", description: "List of missing documents", example: "<ul><li>ID Card</li></ul>" },
  { key: "required_documents", description: "List of required documents", example: "<ul><li>ID Card</li><li>SSN</li></ul>" },
  { key: "expiring_documents", description: "Documents expiring soon", example: "<ul><li>Passport (expires 2026-04-15)</li></ul>" },
  { key: "admin_name", description: "Admin who sent the email", example: "Mark Hillsdon" },
  { key: "support_email", description: "Support email address", example: "support@eventsflo.ai" },
];

// Template types
const TEMPLATE_TYPES = {
  CREW_ONBOARDING_INVITATION: {
    label: "Onboarding Invitation",
    description: "Welcome email with link to set up account",
  },
  CREW_PROFILE_UPDATE_REQUEST: {
    label: "Profile Update Request",
    description: "Request crew to update their profile information",
  },
  CREW_MISSING_DOCUMENTS_REQUEST: {
    label: "Missing Documents Request",
    description: "Request crew to upload missing documents",
  },
  CREW_DOCUMENT_EXPIRY_WARNING: {
    label: "Document Expiry Warning",
    description: "Alert about documents expiring soon",
  },
};

export function CrewEmailEditorModal({
  open,
  onOpenChange,
  templateType,
  crew,
  variables = {},
  onSend,
  title = "Compose Email",
}) {
  // State
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  
  const textareaRef = useRef(null);
  
  // Build variables from crew data and provided variables
  // EF-REMOVE-TEMP-PASSWORD-AND-ENFORCE-SET-PASSWORD-V1: No temp_password in variables
  const buildVariables = useCallback(() => {
    const baseVars = {
      crew_firstname: crew?.first_name || "",
      crew_lastname: crew?.last_name || "",
      crew_fullname: crew ? `${crew.first_name || ""} ${crew.last_name || ""}`.trim() : "",
      crew_email: crew?.email || "",
      company_name: variables.company_name || "Your Company",
      portal_link: variables.portal_link || `${window.location.origin}/crew`,
      missing_documents: variables.missing_documents || "",
      required_documents: variables.required_documents || "",
      expiring_documents: variables.expiring_documents || "",
      admin_name: variables.admin_name || "Admin",
      support_email: variables.support_email || "support@example.com",
    };
    return { ...baseVars, ...variables };
  }, [crew, variables]);
  
  // Load template on open
  useEffect(() => {
    if (open && templateType) {
      loadTemplate();
    }
  }, [open, templateType, language]);
  
  // Load template from backend
  const loadTemplate = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/crew-email-templates?template_type=${templateType}&language=${language}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("eventsflo_auth_token")}`,
          },
        }
      );
      
      if (res.ok) {
        const templates = await res.json();
        if (templates.length > 0) {
          const template = templates[0];
          setSubject(template.subject || "");
          setBody(template.body || "");
        }
      }
    } catch (err) {
      console.error("Load template error:", err);
      toast.error("Failed to load email template");
    } finally {
      setLoading(false);
    }
  };
  
  // Insert variable at cursor position
  const insertVariable = (variable) => {
    const placeholder = `{{${variable}}}`;
    
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = body.substring(0, start) + placeholder + body.substring(end);
      setBody(newBody);
      
      // Set cursor position after inserted text
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + placeholder.length;
        textarea.focus();
      }, 0);
    } else {
      // Append to end if no cursor position
      setBody(body + placeholder);
    }
    
    toast.success(`Inserted {{${variable}}}`);
  };
  
  // Preview email
  const handlePreview = async () => {
    setLoading(true);
    try {
      const vars = buildVariables();
      
      const res = await fetch(`${API_URL}/api/crew-email-templates/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("eventsflo_auth_token")}`,
        },
        body: JSON.stringify({
          template_type: templateType,
          language,
          variables: vars,
          custom_subject: subject,
          custom_body: body,
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setPreviewSubject(data.subject);
        setPreviewHtml(data.body);
        setShowPreview(true);
      } else {
        toast.error("Failed to generate preview");
      }
    } catch (err) {
      console.error("Preview error:", err);
      toast.error("Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };
  
  // Send email
  const handleSend = async () => {
    if (!crew?.email) {
      toast.error("Crew email is required");
      return;
    }
    
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    
    setSending(true);
    try {
      const vars = buildVariables();
      
      // Call the onSend callback with template data
      if (onSend) {
        await onSend({
          template_type: templateType,
          language,
          subject,
          body,
          variables: vars,
          to_email: crew.email,
        });
        toast.success("Email sent successfully");
        onOpenChange(false);
      }
    } catch (err) {
      console.error("Send error:", err);
      toast.error(err.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };
  
  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setShowPreview(false);
      setPreviewHtml("");
      setPreviewSubject("");
    }
  }, [open]);
  
  const templateInfo = TEMPLATE_TYPES[templateType] || {};
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="crew-email-editor-modal"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-primary" />
            {title}
            {templateInfo.label && (
              <Badge variant="outline" className="text-xs">
                {templateInfo.label}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex">
          {/* Editor or Preview */}
          {showPreview ? (
            <div className="flex-1 flex flex-col">
              {/* Preview Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-surface-elevated/30">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Email Preview</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(false)}
                >
                  <X className="h-4 w-4 mr-2" />
                  Back to Editor
                </Button>
              </div>
              
              {/* Preview Content */}
              <ScrollArea className="flex-1 p-4">
                <div className="bg-white rounded-lg p-4 text-gray-900">
                  <div className="border-b pb-3 mb-4">
                    <p className="text-sm text-gray-500">To: {crew?.email}</p>
                    <p className="font-medium">{previewSubject}</p>
                  </div>
                  <div 
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                    className="prose prose-sm max-w-none"
                  />
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="flex-1 flex flex-col">
              {/* Recipient Info */}
              <div className="px-4 py-3 border-b border-white/10 bg-surface-elevated/30">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">To:</span>
                    {crew?.email ? (
                      <Badge variant="secondary" className="font-normal">
                        {crew.first_name} {crew.last_name} &lt;{crew.email}&gt;
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="font-normal">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        No email address
                      </Badge>
                    )}
                  </div>
                  
                  {/* Language Selector */}
                  <div className="flex items-center gap-2 ml-auto">
                    <Label className="text-xs text-muted-foreground">Language:</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger className="w-24 h-8" data-testid="email-language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1: Email contents help (for onboarding invitations) */}
              {templateType === "CREW_ONBOARDING_INVITATION" && (
                <div className="px-4 pt-3">
                  <CrewOnboardingHelpPanel variant="compact" />
                </div>
              )}
              
              {/* Editor Body */}
              <ScrollArea className="flex-1">
                <div className="p-4 space-y-4">
                  {/* Subject */}
                  <div className="space-y-1.5">
                    <Label className="text-sm">Subject</Label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Email subject..."
                      data-testid="email-subject-input"
                      disabled={loading}
                    />
                  </div>
                  
                  {/* Message Body */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Message</Label>
                      
                      {/* Variable Insertion Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" data-testid="insert-variable-btn">
                            <Variable className="h-4 w-4 mr-2" />
                            Insert Variable
                            <ChevronDown className="h-3 w-3 ml-2" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-y-auto">
                          {TEMPLATE_VARIABLES.map((v) => (
                            <DropdownMenuItem
                              key={v.key}
                              onClick={() => insertVariable(v.key)}
                              className="flex flex-col items-start py-2"
                            >
                              <span className="font-mono text-primary text-xs">{`{{${v.key}}}`}</span>
                              <span className="text-xs text-muted-foreground">{v.description}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <Textarea
                      ref={textareaRef}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Email body (HTML supported)..."
                      className="min-h-[300px] font-mono text-sm"
                      data-testid="email-body-textarea"
                      disabled={loading}
                    />
                    
                    {/* Info about variables */}
                    <div className="flex items-start gap-2 p-2 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-300">
                      <Info className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>
                        Use <code className="bg-blue-500/20 px-1 rounded">{`{{variable_name}}`}</code> syntax to insert dynamic content. 
                        Variables will be replaced with actual values when the email is sent.
                      </span>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <DialogFooter className="shrink-0 px-6 py-4 border-t border-white/10 bg-surface-elevated/30">
          <div className="flex items-center justify-between w-full">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            
            <div className="flex items-center gap-3">
              {!showPreview && (
                <Button
                  variant="outline"
                  onClick={handlePreview}
                  disabled={loading || sending}
                  data-testid="preview-email-btn"
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              )}
              
              <Button
                onClick={handleSend}
                disabled={loading || sending || !crew?.email}
                data-testid="send-email-btn"
              >
                {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Send className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CrewEmailEditorModal;
